/**
 * The shared language of Spotiguessr. Terms here are defined in /CONTEXT.md —
 * keep the two in step, and prefer the glossary's word over a synonym.
 */

/**
 * The fixed sequence of Clip durations a Round steps through, in seconds.
 *
 * Six rungs, deliberately: Score is derived from the rung count, so changing the
 * length would silently redefine what a shared Score means.
 */
export const LADDER = [0.5, 1, 2, 5, 12, 30] as const;

export type LadderDuration = (typeof LADDER)[number];

/** How many Steps a Round has before the Reveal. */
export const STEPS_PER_ROUND = LADDER.length;

/** How many Slots make up a Daily Set. */
export const SLOTS_PER_SET = 5;

/** The best possible Score: every Slot solved on the first Step. */
export const PERFECT_SCORE = SLOTS_PER_SET;

/** The worst possible Score: every Slot walked to the Reveal. */
export const WORST_SCORE = SLOTS_PER_SET * STEPS_PER_ROUND;

/** A Pool smaller than this is never offered to the player. */
export const MIN_POOL_SIZE = 50;

/** Countries a Track must be licensed in to enter the Catalog. */
export const MARKETS = ["MK", "GR", "ES", "FR"] as const;
export type Market = (typeof MARKETS)[number];

export const DECADES = ["pre-2000s", "2000s", "2010s", "2020s"] as const;
export type Decade = (typeof DECADES)[number];

export const GENRES = [
  "pop",
  "rock",
  "hip-hop",
  "electronic",
  "rnb",
  "alternative",
  "metal",
  "latin",
  // Deezer has no Country genre id (verified against /genre — 22 genres, no
  // Country), so this is sourced from editorial playlists only. If that yields
  // too few Tracks, the Pool floor makes it unreachable rather than broken.
  "country",
] as const;
export type Genre = (typeof GENRES)[number];

/** A single recorded song the game can draw from. */
export interface Track {
  /** Deezer track id. The Catalog's primary key. */
  id: number;
  title: string;
  /** Primary artist — the one an Artist Match Verdict compares against. */
  artist: string;
  artistId: number;
  /** Carried purely to enable the future Spotify bridge. See docs/future-work.md. */
  isrc: string | null;
  artworkUrl: string | null;
  decade: Decade;
  genres: Genre[];
  /** Composite recognisability, 0–1. Never `track.rank` — see docs/catalog-harvest.md. */
  recognisability: number;
  /**
   * Where this Track sits among its own decade's Tracks, 0–1.
   *
   * Decade-relative on purpose: a 1985 hit and a 2024 hit sit at wildly
   * different absolute fan counts, so a global percentile would mark the whole
   * pre-2000s Pool as obscure. This is what the Daily Set's difficulty ramp
   * draws against — see src/game/daily.ts.
   */
  percentile: number;
  /**
   * Seconds into the Clip where a Step starts playing. Computed at harvest time
   * so every player hears the same instant — see docs/adr/0005.
   */
  hookStart: number;
}

/**
 * The five positions in a Daily Set. Four are era-bound so a Set always spans
 * eras; the wildcard draws from anywhere.
 */
export const SLOT_DECADES: readonly (Decade | "wildcard")[] = [
  "2020s",
  "2010s",
  "2000s",
  "pre-2000s",
  "wildcard",
];

/** The outcome of a Guess. */
export type Verdict = "exact" | "artist-match" | "miss";

/** A Guess the player has spent, with its Verdict. */
export interface SpentStep {
  /** The Track guessed, or null if the player used a Skip. */
  guessedTrackId: number | null;
  verdict: Verdict | "skipped";
}

export interface CatalogMeta {
  builtAt: string;
  markets: readonly Market[];
  trackCount: number;
}

export interface Catalog {
  meta: CatalogMeta;
  tracks: Track[];
}
